/* eslint-disable react-refresh/only-export-components --
 * The provider and the consumer hooks are intrinsically paired and live in
 * one module by design (matches workshop/shell/PendingEdits.jsx). Splitting
 * them buys nothing structural and breaks the side-by-side port.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";

// Global pending-edits store for inline editing. Each screen registers a
// 'scope' via usePendingScope:
//   - dirty: are there unsaved local edits?
//   - save():    persists (Promise) - called by the SaveBar, by
//                UnsavedGuard's dialog when the reader leaves, and by
//                the acting-company gate (unsaved-edits-gate.ts)
//   - discard(): drops local edits
//
// The SaveBar reads all scopes and offers a single Verwerfen/Speichern
// flow for the whole app. One consistent save shape across all pages —
// except a scope flagged `ownSaveAction`, which its screen saves with its
// own button (and the dialog on leaving); the SaveBar skips it.
//
// Ported node-by-node from workshop/shell/PendingEdits.jsx.

export interface ScopeState {
  dirty: boolean;
  awaitingConfirmation?: boolean;
  /**
   * The screen has its own save action (a form's Publish, a comment's Send), so the SaveBar stays
   * out of it: a second "Save" beside the screen's own would offer two buttons for one write. The
   * unsaved-changes dialog and the acting-company gate still ask — they fire when the reader is
   * leaving, where the screen's own button is no longer in reach.
   */
  ownSaveAction?: boolean;
  /**
   * Nothing here can be saved on the way out: the screen's own action is the only way to keep it, such as the roleplay
   * game's unsent move, which only Send sends (spark#1075). The unsaved-changes dialog then offers only stay and leave
   * when every dirty scope is like this, and `save` is never called. Implies `ownSaveAction` for the SaveBar.
   */
  leaveOnly?: boolean;
  /**
   * Persist this scope's edits, or REJECT WITH A `LocalizedError` saying why not
   * (`@/lib/localized-error`).
   *
   * The unsaved-changes dialog shows that message, so it has to be product copy the reader can act
   * on — already translated, never genesis's own prose (@adr SPARK-0010-no-raw-server-errors-in-ui@1). Rejecting with any other error
   * is allowed and safe: the dialog falls back to a generic "Save failed." and logs the real one.
   * It is simply less useful, so a deliberate refusal should always be a LocalizedError.
   */
  save: () => void | Promise<void>;
  discard: () => void;
}

interface ContextValue {
  scopes: Record<string, ScopeState>;
  setScope: (id: string, patch: Partial<ScopeState>) => void;
  removeScope: (id: string) => void;
  /**
   * Monotonically-increasing counter, bumped by the SaveBar after every
   * successful "Speichern" cycle. Inline-edit primitives (EditableRow) watch
   * this tick to auto-collapse open editors once the user has saved.
   */
  saveTick: number;
  /** Bump `saveTick` after a successful save. Called by the SaveBar. */
  signalSaved: () => void;
  /** True while any scope's save is running — its scope stays dirty until it lands. */
  saving: boolean;
  /**
   * Count a save the screen runs from its own button (an `ownSaveAction` scope's Publish or Send)
   * as running, exactly like a scope save. `saving` then holds the unsaved-changes dialog's buttons
   * closed until it lands: a Save offered meanwhile could only join that write, and would report
   * success for edits made after the click that the write never carried.
   */
  trackSave: <T,>(work: Promise<T>) => Promise<T>;
  /**
   * Whether UnsavedGuard is holding a navigation away. A screen whose own write lands meanwhile must
   * not navigate itself: the router keeps one blocked navigation, so the screen's would replace the
   * reader's, and the guard would then complete the screen's instead. Read at call time, because the
   * write that asks resolves long after the render that started it.
   */
  isLeaveHeld: () => boolean;
  /** Set by UnsavedGuard alone. */
  setLeaveHeld: (held: boolean) => void;
  /**
   * Whether any scope is dirty, read at call time. `scopes` reaches its readers a render later, too
   * late for UnsavedGuard's blocker when a screen clears its scope and navigates in one handler.
   */
  hasDirtyScope: () => boolean;
}

const PendingEditsContext = createContext<ContextValue | null>(null);

export function PendingEditsProvider({ children }: { children: ReactNode }) {
  const [scopes, setScopes] = useState<Record<string, ScopeState>>({});
  // A render ahead of `scopes`, which is what hasDirtyScope reads.
  const latestScopes = useRef(scopes);
  const commitScopes = useCallback((next: Record<string, ScopeState>) => {
    latestScopes.current = next;
    setScopes(next);
  }, []);
  const [saveTick, setSaveTick] = useState(0);
  const [running, setRunning] = useState(0);

  const setScope = useCallback((id: string, patch: Partial<ScopeState>) => {
    const { save } = patch;
    const counted: Partial<ScopeState> = save
      ? {
          ...patch,
          save: async () => {
            setRunning((n) => n + 1);
            try {
              await save();
            } finally {
              setRunning((n) => n - 1);
            }
          },
        }
      : patch;
    const s = latestScopes.current;
    commitScopes({
      ...s,
      [id]: { ...(s[id] ?? { dirty: false, save: noop, discard: noop }), ...counted },
    });
  }, [commitScopes]);

  const removeScope = useCallback((id: string) => {
    if (!(id in latestScopes.current)) return;
    const next = { ...latestScopes.current };
    delete next[id];
    commitScopes(next);
  }, [commitScopes]);

  const hasDirtyScope = useCallback(() => Object.values(latestScopes.current).some((s) => s.dirty), []);

  const signalSaved = useCallback(() => {
    setSaveTick((t) => t + 1);
  }, []);

  const trackSave = useCallback(<T,>(work: Promise<T>): Promise<T> => {
    setRunning((n) => n + 1);
    return work.finally(() => setRunning((n) => n - 1));
  }, []);

  const leaveHeld = useRef(false);
  const isLeaveHeld = useCallback(() => leaveHeld.current, []);
  const setLeaveHeld = useCallback((held: boolean) => {
    leaveHeld.current = held;
  }, []);

  const value = useMemo<ContextValue>(
    () => ({
      scopes,
      setScope,
      removeScope,
      saveTick,
      signalSaved,
      saving: running > 0,
      trackSave,
      isLeaveHeld,
      setLeaveHeld,
      hasDirtyScope,
    }),
    [scopes, setScope, removeScope, saveTick, signalSaved, running, trackSave, isLeaveHeld, setLeaveHeld, hasDirtyScope],
  );

  return <PendingEditsContext.Provider value={value}>{children}</PendingEditsContext.Provider>;
}

export function usePendingEdits(): ContextValue | null {
  return useContext(PendingEditsContext);
}

interface PendingScopeHandlers {
  dirty: boolean;
  awaitingConfirmation?: boolean;
  ownSaveAction?: boolean;
  leaveOnly?: boolean;
  save: () => void | Promise<void>;
  discard: () => void;
}

// Screen hook: registers dirty status + save/discard with the store.
// save/discard are kept current via a ref (no re-registration needed).
// Matches workshop/shell/PendingEdits.jsx node-by-node.
//
// Returns `settle`, which marks the scope clean in the store at once, for a screen that navigates in
// the same handler as its own write: the effect below would tell the store only after the blocker had
// asked. A no-op once the screen has unmounted, because every instance of a screen shares the id and
// a write that lands late must not clear the draft of a newer one.
export function usePendingScope(id: string, handlers: PendingScopeHandlers): () => void {
  const ctx = useContext(PendingEditsContext);
  const ref = useRef(handlers);

  const setScope = ctx?.setScope;
  const removeScope = ctx?.removeScope;
  const { dirty, save, discard, awaitingConfirmation = false, leaveOnly = false } = handlers;
  const ownSaveAction = handlers.ownSaveAction === true || leaveOnly;

  useEffect(() => {
    ref.current = { dirty, save, discard, awaitingConfirmation, ownSaveAction, leaveOnly };
  }, [dirty, save, discard, awaitingConfirmation, ownSaveAction, leaveOnly]);

  useEffect(() => {
    if (!setScope) return;
    setScope(id, {
      dirty,
      awaitingConfirmation,
      ownSaveAction,
      leaveOnly,
      save: () => ref.current.save(),
      discard: () => ref.current.discard(),
    });
  }, [id, dirty, awaitingConfirmation, ownSaveAction, leaveOnly, setScope]);

  const held = useRef(false);
  useEffect(() => {
    if (!removeScope) return;
    held.current = true;
    return () => {
      held.current = false;
      removeScope(id);
    };
  }, [id, removeScope]);

  return useCallback(() => {
    if (held.current) setScope?.(id, { dirty: false });
  }, [id, setScope]);
}

function noop() {
  /* default scope handler */
}
